import React from "react";
import { Button } from "@/components/ui/Button";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { TicketStatus } from "@/services/LiveQueueService";
import Pagination from "@/components/ui/table/PaginationTable";
import { Ticket, TicketTable } from "@/app/types/tickets";
import { SearchCriteria } from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { TicketItem } from "./TicketItem";

interface TicketListProps<T extends SearchCriteria> {
  searchQuery: string;
  setSearchQuery: (search: string) => void;
  selectedStatus: TicketStatus | null;
  setSelectedStatus: (status: TicketStatus | null) => void;
  statusOptions: {value: string; label: string}[];
  departmentData: TicketTable | undefined;
  isError: boolean;
  isDepartmentDataLoading: boolean;
  searchCriteria: T;
  setSearchCriteria: React.Dispatch<React.SetStateAction<T>>;
  totalRecords: number;
  renderItem: (ticket: Ticket) => React.ReactNode
}
export function TicketList<T extends SearchCriteria & {limit: number, offset: number}>({
  searchQuery,
  setSearchQuery,
  selectedStatus,
  setSelectedStatus,
  statusOptions,
  departmentData,
  isError,
  isDepartmentDataLoading,
  searchCriteria,
  setSearchCriteria,
  totalRecords,
  renderItem,
}: TicketListProps<T>) {
  const navigate = useNavigate();
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="min-h-screen relative">
      <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-2">
        <Button
          onClick={() => {
            navigate("/user/home");
          }}
          variant={"ghost"}
          className="w-16 h-8 border-2"
        >
          {" "}
          <ChevronLeftIcon className="h-4 w-5" />
        </Button>
        <div></div>
        {/* Search Input */}

        <div>
          <Input
            type="search"
            placeholder="Search ticket"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.trim())}
            className="w-full"
          />
        </div>

        {/* Status Dropdown */}
        <div>
          <select
            value={selectedStatus || "All"} // Default to "All"
            onChange={(e) => {
              const value = e.target.value as string;
              setSelectedStatus(
                value === "All" ? null : (value as TicketStatus)
              );
            }}
            // className="w-full p-2 border rounded"
            className="flex h-10 w-full bg-white items-center relative justify-between whitespace-nowrap rounded-md border border-checkboxborder bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
          >
            <option value="All">Show All</option> {/* Add "Show All" Option */}
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Ticket List */}
      <div className="p-2 space-y-3">
        <div className="h-[32rem] overflow-x-auto fancy-scrollbar">
          {(departmentData?.data.length ?? 0) === 0 &&
          !isDepartmentDataLoading &&
          !isError ? (
            <div className="text-center text-gray-500 mt-4">
              No tickets match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
              {/* {departmentData?.data.map((ticket) */}
              {departmentData?.data
                .filter(
                  (ticket) =>
                    !ticket.ticket_number.toLowerCase().includes("r") ||
                    (!ticket.ticket_number.toLowerCase().includes("R") &&
                      ticket.status !== TicketStatus.COMPLETED)
                )
                .map((ticket) => renderItem(ticket))}
            </div>
          )}
        </div>
        <footer className="fixed bottom-0 left-0 w-full text-center p-4 shadow-lg">
          {(departmentData?.data?.length ?? 0) > 0 && (
            <Pagination
              limit={searchCriteria.limit}
              offset={searchCriteria.offset}
              totalItems={totalRecords}
              onLimitChange={handleLimitChange}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              onFirstPage={handleFirstPage}
              onLastPage={handleLastPage}
              isLastPage={isLastPage}
            />
          )}
        </footer>
      </div>
    </div>
  );
}
